'use client';
import React from 'react';
import { Thermometer, Cloud, ArrowUp, Waves, GitMerge, Clock, Wind, CloudRain, CloudSnow } from 'lucide-react';
import { DailySoaringForecast } from '@/lib/mockWeatherData';
import MetricCard from '@/components/ui/MetricCard';
import { useSettings } from '@/context/SettingsContext';

interface SoaringConditionsGridProps {
  forecast: DailySoaringForecast;
}

export default function SoaringConditionsGrid({ forecast }: SoaringConditionsGridProps) {
  const { formatAltitude, formatVZ, formatWind, formatTemp } = useSettings();

  // Determine dominant sky type from hourly data
  const convectiveHours = forecast.hourly?.filter((h) => h.isConvective) ?? [];
  const cumulusCount = convectiveHours.filter((h) => h.skyType === 'cumulus').length;
  const blueCount = convectiveHours.filter((h) => h.skyType === 'blue').length;
  const dominantSkyType: 'cumulus' | 'blue' | 'stable' =
    convectiveHours.length === 0
      ? 'stable'
      : cumulusCount >= blueCount
      ? 'cumulus' : 'blue';

  const skyLabel =
    dominantSkyType === 'cumulus' ? '☁ Cumulus'
      : dominantSkyType === 'blue' ? '☀ Thermiques purs' : 'Stable / Pas de thermiques';

  const skyColor =
    dominantSkyType === 'cumulus' ? 'var(--purple-cloud)' : dominantSkyType === 'blue' ? 'var(--warning-orange)' : 'var(--text-muted)';

  // Convection window duration
  const convStart = parseInt(forecast.convectionStart.split(':')[0]) || 0;
  const convEnd = parseInt(forecast.convectionEnd.split(':')[0]) || 0;
  const convStartMin = parseInt(forecast.convectionStart.split(':')[1]) || 0;
  const convEndMin = parseInt(forecast.convectionEnd.split(':')[1]) || 0;
  const durationH =
    forecast.convectionStart !== '--' && forecast.convectionEnd !== '--'
      ? Math.round(((convEnd * 60 + convEndMin) - (convStart * 60 + convStartMin)) / 60 * 10) / 10
      : 0;

  // Wind gust: show if gust > surface speed * 1.25 (significant)
  const gustSignificant = forecast.windGust != null && forecast.windGust > forecast.windSurface.speed * 1.25;

  // Formatted values
  const altFmt = forecast.maxCumulusBase > 0 ? formatAltitude(forecast.maxCumulusBase) : null;
  const vzFmt = forecast.maxVZ > 0 ? formatVZ(forecast.maxVZ) : null;
  const windFmt = formatWind(forecast.windSurface.speed);
  const gustFmt = forecast.windGust != null ? formatWind(forecast.windGust) : null;
  const tempMinFmt = forecast.tempMin != null ? formatTemp(forecast.tempMin) : null;
  const tempMaxFmt = forecast.tempMax != null ? formatTemp(forecast.tempMax) : null;
  const triggerFmt = formatTemp(forecast.triggerTemp);
  const precipProb = forecast.precipProbabilityMax
    ?? Math.max(0, ...(forecast.hourly ?? []).map((h) => h.precipProbability ?? 0));
  const precipMm = forecast.precipTotalMm
    ?? Math.round((forecast.hourly ?? []).reduce((sum, h) => sum + (h.precipAmount ?? 0), 0) * 10) / 10;
  const rainColor = precipMm >= 5 || precipProb >= 70 ? 'var(--danger-red)' : precipMm >= 1 || precipProb >= 35 ? 'var(--warning-orange)' : 'var(--success-green)';
  const rainLabel = forecast.rainRiskLabel ?? (precipProb >= 35 ? 'Averses possibles' : 'Pas de pluie significative');

  const widgetBorder = '1px solid var(--card-border)';
  const widgetBg = 'var(--card)';

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-3">
      {/* Convection window */}
      <MetricCard
        label="Fenêtre Convective"
        value={forecast.convectionStart !== '--' ? forecast.convectionStart : '--'}
        unit={forecast.convectionEnd !== '--' ? `→ ${forecast.convectionEnd}` : ''}
        icon={<Clock size={16} />}
        color="var(--success-green)"
        subtext={
          durationH > 0
            ? `Durée : ${durationH}h de thermiques`
            : 'Gradient < 0.6°C/100m — atmosphère stable'
        }
      />

      {/* Cumulus base with sky type — dual unit */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{
          backgroundColor: widgetBg,
          border: forecast.maxCumulusBase > 0 ? '1px solid rgba(126,200,227,0.3)' : widgetBorder,
          boxShadow: forecast.maxCumulusBase > 1500 ? '0 0 16px rgba(126,200,227,0.1)' : 'none',
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            Plafond Exploitable
          </span>
          <Cloud size={16} style={{ color: skyColor }} />
        </div>
        {altFmt ? (
          <>
            <div className="flex items-end gap-2">
              <span className="font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>{altFmt.primary}</span>
              <span className="text-sm mb-0.5" style={{ color: 'var(--text-secondary)' }}>{altFmt.primaryUnit}</span>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{altFmt.secondary} {altFmt.secondaryUnit}</p>
          </>
        ) : (
          <span className="font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>--</span>
        )}
        <div className="flex items-center justify-between">
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Lecture emagramme horaire
          </p>
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${skyColor}20`, color: skyColor, border: `1px solid ${skyColor}40` }}
          >
            {skyLabel}
          </span>
        </div>
      </div>

      {/* Max VZ — dual unit */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{
          backgroundColor: widgetBg,
          border: widgetBorder,
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            VZ Maxi Estimée
          </span>
          <ArrowUp size={16} style={{ color: forecast.maxVZ >= 3 ? 'var(--success-green)' : forecast.maxVZ >= 1.5 ? 'var(--warning-orange)' : 'var(--danger-red)' }} />
        </div>
        {vzFmt ? (
          <>
            <div className="flex items-end gap-2">
              <span className="font-bold text-2xl" style={{ color: forecast.maxVZ >= 3 ? 'var(--success-green)' : forecast.maxVZ >= 1.5 ? 'var(--warning-orange)' : 'var(--danger-red)' }}>
                {vzFmt.primary}
              </span>
              <span className="text-sm mb-0.5" style={{ color: 'var(--text-secondary)' }}>{vzFmt.primaryUnit}</span>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{vzFmt.secondary} {vzFmt.secondaryUnit}</p>
          </>
        ) : (
          <span className="font-bold text-2xl" style={{ color: 'var(--text-muted)' }}>--</span>
        )}
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {forecast.maxVZ > 0
            ? `${forecast.maxVZ >= 3 ? 'Thermiques puissants' : forecast.maxVZ >= 1.5 ? 'Thermiques modérés' : 'Thermiques faibles'}${forecast.highCloudFilter ? ' · ↓ Cirrus' : ''}`
            : 'Couche instable < 500m'}
        </p>
      </div>

      {/* Trigger temp — dual unit */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{ backgroundColor: widgetBg, border: widgetBorder }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            Temp. Déclenchement
          </span>
          <Thermometer size={16} style={{ color: 'var(--warning-orange)' }} />
        </div>
        <div className="flex items-end gap-2">
          <span className="font-bold text-2xl" style={{ color: 'var(--warning-orange)' }}>{triggerFmt.primary}</span>
          <span className="text-sm mb-0.5" style={{ color: 'var(--text-secondary)' }}>{triggerFmt.primaryUnit}</span>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{triggerFmt.secondary} {triggerFmt.secondaryUnit} · T sol min. pour thermiques</p>
      </div>

      {/* Min / Max temperature widget — dual unit */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{ backgroundColor: widgetBg, border: widgetBorder }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            T° Sol Min / Max
          </span>
          <Thermometer size={16} style={{ color: 'var(--info-blue)' }} />
        </div>
        <div className="flex items-end gap-3">
          <div className="flex flex-col">
            <span className="text-xs" style={{ color: 'var(--info-blue)' }}>Min</span>
            <span className="font-bold text-xl" style={{ color: 'var(--info-blue)' }}>
              {tempMinFmt ? `${tempMinFmt.primary}${tempMinFmt.primaryUnit}` : '--'}
            </span>
            {tempMinFmt && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{tempMinFmt.secondary}{tempMinFmt.secondaryUnit}</span>}
          </div>
          <span className="text-lg mb-0.5" style={{ color: 'var(--text-muted)' }}>/</span>
          <div className="flex flex-col">
            <span className="text-xs" style={{ color: 'var(--warning-orange)' }}>Max</span>
            <span className="font-bold text-xl" style={{ color: 'var(--warning-orange)' }}>
              {tempMaxFmt ? `${tempMaxFmt.primary}${tempMaxFmt.primaryUnit}` : '--'}
            </span>
            {tempMaxFmt && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{tempMaxFmt.secondary}{tempMaxFmt.secondaryUnit}</span>}
          </div>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Températures au sol prévues (2m)</p>
      </div>

      {/* Surface wind with gusts — dual unit */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{
          backgroundColor: widgetBg,
          border: gustSignificant ? '1px solid rgba(248,113,113,0.25)' : widgetBorder,
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            Vent Sol (10m)
          </span>
          <Wind size={16} style={{ color: gustSignificant ? 'var(--danger-red)' : 'var(--text-secondary)' }} />
        </div>
        <div className="flex items-end gap-2">
          <span className="font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>{windFmt.primary}</span>
          <span className="text-sm mb-0.5" style={{ color: 'var(--text-secondary)' }}>{windFmt.primaryUnit} {forecast.windSurface.dir}</span>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{windFmt.secondary} {windFmt.secondaryUnit} · Standard OMM 10m</p>
        {gustSignificant && gustFmt && (
          <p className="text-xs font-semibold" style={{ color: 'var(--danger-red)' }}>
            Rafales : {gustFmt.primary} {gustFmt.primaryUnit} ({gustFmt.secondary} {gustFmt.secondaryUnit})
          </p>
        )}
      </div>

      {/* Rain / showers */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{
          backgroundColor: widgetBg,
          border: precipProb >= 35 || precipMm >= 1 ? `1px solid ${rainColor}55` : widgetBorder,
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            Pluie / Averses
          </span>
          <CloudRain size={16} style={{ color: rainColor }} />
        </div>
        <div className="flex items-end gap-2">
          <span className="font-bold text-2xl" style={{ color: rainColor }}>{precipProb}</span>
          <span className="text-sm mb-0.5" style={{ color: 'var(--text-secondary)' }}>%</span>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{precipMm.toFixed(1)} mm prevus</p>
        <p className="text-xs font-semibold" style={{ color: rainColor }}>{rainLabel}</p>
      </div>

      {/* Wave */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{
          backgroundColor: widgetBg,
          border: forecast.waveConditions ? '1px solid rgba(126,200,227,0.3)' : widgetBorder,
          boxShadow: forecast.waveConditions ? '0 0 16px rgba(126,200,227,0.1)' : 'none',
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            Onde de Montagne
          </span>
          <Waves size={16} style={{ color: forecast.waveConditions ? 'var(--info-blue)' : 'var(--text-muted)' }} />
        </div>
        <span className="font-bold text-2xl" style={{ color: forecast.waveConditions ? 'var(--info-blue)' : 'var(--text-muted)' }}>
          {forecast.waveConditions ? 'Possible' : 'Non détectée'}
        </span>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {forecast.waveConditions ? "Conditions de vent favorables à l'onde" : 'Vent insuffisant pour onde'}
        </p>
      </div>

      {/* Convergence */}
      <div
        className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
        style={{
          backgroundColor: widgetBg,
          border: forecast.convergence ? '1px solid rgba(251,191,36,0.3)' : widgetBorder,
          boxShadow: forecast.convergence ? '0 0 16px rgba(251,191,36,0.1)' : 'none',
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
            Confluence / Conv.
          </span>
          <GitMerge size={16} style={{ color: forecast.convergence ? 'var(--warning-orange)' : 'var(--text-muted)' }} />
        </div>
        <span className="font-bold text-2xl" style={{ color: forecast.convergence ? 'var(--warning-orange)' : 'var(--text-muted)' }}>
          {forecast.convergence ? 'Active' : 'Absente'}
        </span>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {forecast.convergence ? 'Ligne de convergence détectée' : 'Pas de convergence significative'}
        </p>
      </div>

      {/* High cloud alert */}
      {forecast.highCloudFilter && (
        <div
          className="rounded-xl p-4 flex flex-col gap-2 transition-all duration-200"
          style={{
            backgroundColor: widgetBg,
            border: '1px solid rgba(148,163,184,0.25)',
          }}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>
              Nuages Hauts
            </span>
            <CloudSnow size={16} style={{ color: 'var(--text-secondary)' }} />
          </div>
          <span className="font-bold text-2xl" style={{ color: 'var(--text-secondary)' }}>
            {forecast.highCloudCover}%
          </span>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {forecast.highCloudCover > 60
              ? "Voile d'altitude détecté : activité thermique limitée" :'Soleil voilé — VZ réduite de 20%'}
          </p>
        </div>
      )}
    </div>
  );
}
